import React, { useState, useEffect } from 'react';
import {
  Box,Typography,Card,CardContent,List,ListItem,ListItemText,Chip, Dialog, DialogTitle,DialogContent,DialogActions,Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Receipt as ExpenseIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { api } from '../../../../services/api';
import * as yup from 'yup';

interface TripExpense {
  id?: number;
  description: string;
  amount: number;
  category: 'FUEL' | 'TOLL' | 'PARKING' | 'MAINTENANCE' | 'DRIVER ALLOWANCE'| 'OTHER';
  date: string;
  receipt?: string;
  notes?: string;
  // Additional fields from API response
  place?: string;
  fuelQty?: number;
  odometerReading?: number;
  expenseType?: string;
  datetime?: string;
  comment?: string;
}

interface TripExpensesSectionProps {
  expenses: TripExpense[];
  onUpdate: (updatedExpenses: TripExpense[]) => Promise<void>;
  onDeleteExpense?: (expenseId: number) => Promise<void>;
  loading?: boolean;
  tripCode: string; // Use tripCode instead of tripId
  disabled?: boolean
}

// Validation schema
const expenseValidationSchema = yup.object().shape({
  notes: yup.string().when('expenseType', {
    is: 'DRIVER ALLOWANCE',
    then: (schema) => schema.trim().required('Notes are required for Driver Allowance'),
    otherwise: (schema) => schema.notRequired()
  })
});

const TripExpensesSection: React.FC<TripExpensesSectionProps> = ({
  expenses,
  onUpdate,
  onDeleteExpense,
  loading = false,
  tripCode,
  disabled = false
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<TripExpense | null>(null);
  const [localExpenses, setLocalExpenses] = useState<TripExpense[]>(expenses);
  const [expenseForm, setExpenseForm] = useState({
    expenseType: 'FUEL',
    amount: 0,
    notes: '',
    fuelQty: 0,
    odometerReading: 0
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [notesError, setNotesError] = useState<string>('');

  // Update local expenses when props change
  useEffect(() => {
    setLocalExpenses(expenses);
  }, [expenses]);

  const totalExpenses = localExpenses.reduce((sum, expense) => sum + expense.amount, 0);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'FUEL': return '#ef4444';
      case 'TOLL': return '#3b82f6';
      case 'PARKING': return '#8b5cf6';
      case 'MAINTENANCE': return '#f59e0b';
      case 'DRIVER ALLOWANCE': return '#10b981';
      default: return '#6b7280';
    }
  };

  const mapApiResponseToExpense = (apiExpense: any): TripExpense => {
    return {
      id: apiExpense.id,
      description: apiExpense.place || 
        (apiExpense.expenseType === 'FUEL' && apiExpense.fuelQty ? `${apiExpense.fuelQty}L` : ''),
      amount: apiExpense.amount || 0,
      category: apiExpense.expenseType || 'OTHER',
      date: apiExpense.datetime ? new Date(apiExpense.datetime).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      notes: apiExpense.comment || '',
      place: apiExpense.place,
      fuelQty: apiExpense.fuelQty,
      odometerReading: apiExpense.odometerReading,
      expenseType: apiExpense.expenseType,
      datetime: apiExpense.datetime,
      comment: apiExpense.comment
    };
  };

  const mappedExpenses = localExpenses.map(expense => {
    // If expense already has our expected fields, return as is
    if (expense.description && expense.category && !expense.expenseType) {
      return expense;
    }
    // Otherwise, map from API response format
    return mapApiResponseToExpense(expense);
  });

  const SectionHeader = () => (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        backgroundColor: '#464545',
        color: 'white',
        p: 2,
        borderRadius: '8px 8px 0 0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ExpenseIcon />
        <Typography variant="h6" fontWeight="bold">
          TRIP EXPENSES (₹{totalExpenses.toFixed(2)})
        </Typography>
      </Box>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setIsDialogOpen(true)}
        disabled={loading || disabled}
        sx={{ 
          backgroundColor: 'rgba(255,255,255,0.2)',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
        }}
      >
        Add Expense
      </Button>
    </Box>
  );

  const handleEditExpense = (expense: TripExpense) => {
    setEditingExpense(expense);
    setExpenseForm({
      expenseType: expense.expenseType || expense.category || 'OTHER',
      amount: expense.amount || 0,
      notes: expense.notes || expense.comment || '',
      fuelQty: expense.fuelQty || 0,
      odometerReading: expense.odometerReading || 0
    });
    setIsDialogOpen(true);
  };

  const handleDeleteExpense = async (expenseId: number) => {
    if (!onDeleteExpense || !expenseId) return;
    
    try {
      setDeleting(expenseId);
      await onDeleteExpense(expenseId);
    } catch (error: any) {
      console.error('Error deleting expense:', error);
      throw error;
    } finally {
      setDeleting(null);
    }
  };

  const handleAddExpense = () => {
    setEditingExpense(null);
    setExpenseForm({
      expenseType: 'FUEL',
      amount: 0,
      notes: '',
      fuelQty: 0,
      odometerReading: 0
    });
    setIsDialogOpen(true);
  };

  const handleSaveExpense = async () => {
    try {
      setSaving(true);
      
      if (!tripCode) {
        alert('Trip Code is required');
        return;
      }

      if (!expenseForm.expenseType || !expenseForm.amount || expenseForm.amount <= 0) {
        alert('Expense type and amount are required');
        return;
      }

      // Validate notes field using yup
      try {
        await expenseValidationSchema.validate(expenseForm, { abortEarly: false });
        setNotesError(''); // Clear error if validation passes
      } catch (validationError: any) {
        if (validationError.inner && validationError.inner.length > 0) {
          const notesErr = validationError.inner.find((err: any) => err.path === 'notes');
          if (notesErr) {
            setNotesError(notesErr.message);
            setSaving(false);
            return;
          }
        }
      }
      
      const requestBody = {
        expenseType: expenseForm.expenseType,
        amount: Number(expenseForm.amount),
        comment: expenseForm.notes || '',
        ...(expenseForm.fuelQty > 0 && { fuelQty: Number(expenseForm.fuelQty) }),
        ...(expenseForm.odometerReading > 0 && { odometerReading: Number(expenseForm.odometerReading) })
      };

      // Close dialog and reset form
      setIsDialogOpen(false);
      setEditingExpense(null);
      setExpenseForm({
        expenseType: 'FUEL',
        amount: 0,
        notes: '',
        fuelQty: 0,
        odometerReading: 0
      });

      // Make API call to save expense
      if (editingExpense?.id) {
        // Update existing expense
        await api.updateTripExpense(tripCode, editingExpense.id, requestBody);
      } else {
        // Add new expense
        await api.addTripExpense(tripCode, requestBody);
      }

      // Trigger parent to fetch fresh data from backend
      await onUpdate([]);

    } catch (error: any) {
      alert(`Error saving expense: ${error?.response?.data?.message || error?.message || error}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card sx={{ mb: 2, backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
        <SectionHeader />
        <CardContent sx={{ backgroundColor: '#161616', color: 'white' }}>
          {mappedExpenses.length > 0 ? (
            <List sx={{ p: 0 }}>
              {mappedExpenses.map((expense, index) => (
                <ListItem 
                  key={expense.id || index}
                  sx={{ px: 0 }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton 
                        size="small"
                        onClick={() => handleEditExpense(expense)}
                        disabled={disabled || deleting !== null}
                        sx={{ 
                          color: '#3b82f6', 
                          '&:disabled': { opacity: 0.5 }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={() => expense.id && handleDeleteExpense(expense.id)}
                        disabled={disabled || deleting !== null || !expense.id || !onDeleteExpense}
                        sx={{ 
                          color: deleting === expense.id ? '#6b7280' : '#ef4444',
                          '&:disabled': { opacity: 0.5 }
                        }}
                      >
                        {deleting === expense.id ? (
                          <CircularProgress size={16} sx={{ color: '#6b7280' }} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip 
                          label={expense.category}
                          size="small"
                          sx={{ 
                            backgroundColor: getCategoryColor(expense.category),
                            color: 'white',
                            fontSize: '0.7rem'
                          }}
                        />
                        {expense.description && (
                          <Typography variant="subtitle1" fontWeight="bold" color="white">
                            {expense.description}
                          </Typography>
                        )}
                        <Typography variant="subtitle1" fontWeight="bold" color="#10b981">
                          ₹{expense.amount.toFixed(2)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Date: {new Date(expense.datetime || expense.date).toLocaleDateString('en-IN')}
                          {expense.place && ` • Place: ${expense.place}`}
                          {expense.fuelQty && ` • Fuel: ${expense.fuelQty}L`}
                          {expense.odometerReading && ` • Odometer: ${expense.odometerReading} km`}
                          {(expense.notes || expense.comment) && ` • ${expense.notes || expense.comment}`}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                No expenses recorded for this trip
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Expense Dialog */}
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1a1a1a', color: 'white' }}>
          {editingExpense ? 'Edit Expense' : 'Add Expense'}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#2a2a2a', color: 'white' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Expense Type
              </Typography>
              <Select
                value={expenseForm.expenseType}
                onChange={(e) => setExpenseForm(prev => ({ ...prev, expenseType: e.target.value }))
                }
                sx={{ 
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#999' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#dc2626' },
                  '& .MuiSvgIcon-root': { color: 'white' }
                }}
              >
                <MenuItem value="FUEL">Fuel</MenuItem>
                <MenuItem value="TOLL">Toll</MenuItem>
                <MenuItem value="PARKING">Parking</MenuItem>
                <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                <MenuItem value="DRIVER ALLOWANCE">Driver Allowance</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={expenseForm.amount || ''}
              onChange={(e) => setExpenseForm(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))
              }
              // Prevent scroll from changing the value
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
              variant="outlined"
              inputProps={{
                // Prevent mousewheel from changing the value when focused
                onWheel: (e) => e.currentTarget.blur()
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  color: 'white',
                  '& fieldset': { borderColor: '#666' },
                  '&:hover fieldset': { borderColor: '#999' },
                  '&.Mui-focused fieldset': { borderColor: '#dc2626' }
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                // Remove number input spinners
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                  '-webkit-appearance': 'none',
                  margin: 0
                },
                '& input[type=number]': {
                  '-moz-appearance': 'textfield'
                }
              }}
            />

            <TextField
              fullWidth
              label={expenseForm.expenseType === 'DRIVER ALLOWANCE' ? 'Notes *' : 'Notes'}
              multiline
              rows={3}
              value={expenseForm.notes || ''}
              onChange={(e) => {
                setExpenseForm(prev => ({ ...prev, notes: e.target.value }));
                if (notesError) setNotesError(''); // Clear error on change
              }}
              variant="outlined"
              error={!!notesError}
              helperText={notesError}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  color: 'white',
                  '& fieldset': { borderColor: '#666' },
                  '&:hover fieldset': { borderColor: '#999' },
                  '&.Mui-focused fieldset': { borderColor: '#dc2626' }
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                '& .MuiFormHelperText-root': { color: '#ef4444' }
              }}
            />

            {expenseForm.expenseType === 'FUEL' && (
              <>
                <TextField
                  fullWidth
                  label="Fuel Quantity (Liters)"
                  type="number"
                  value={expenseForm.fuelQty || ''}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, fuelQty: parseFloat(e.target.value) || 0 }))
                  }
                  variant="outlined"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      color: 'white',
                      '& fieldset': { borderColor: '#666' },
                      '&:hover fieldset': { borderColor: '#999' },
                      '&.Mui-focused fieldset': { borderColor: '#dc2626' }
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                  }}
                />

                <TextField
                  fullWidth
                  label="Odometer Reading (km)"
                  type="number"
                  value={expenseForm.odometerReading || ''}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, odometerReading: parseInt(e.target.value) || 0 }))
                  }
                  variant="outlined"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      color: 'white',
                      '& fieldset': { borderColor: '#666' },
                      '&:hover fieldset': { borderColor: '#999' },
                      '&.Mui-focused fieldset': { borderColor: '#dc2626' }
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                  }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#2a2a2a' }}>
          <Button 
            onClick={() => setIsDialogOpen(false)}
            sx={{ color: 'white' }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveExpense}
            variant="contained"
            sx={{ backgroundColor: '#dc2626' }}
            disabled={saving || !expenseForm.expenseType || !expenseForm.amount || expenseForm.amount <= 0}
          >
            {saving ? <CircularProgress size={20} /> : (editingExpense ? 'Update Expense' : 'Add Expense')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TripExpensesSection;
